import { useEffect, useState } from "react";
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

// Recharts components
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

// RasPm Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";

// Services
import { RequestService } from "services/RequestService";

function StatCard({ title, value, icon, color }) {
  return (
    <MDBox
      p={2}
      sx={{
        backgroundColor: ({ palette: { white }, functions: { rgba } }) => rgba(white.main, 0.8),
        borderRadius: 2,
        boxShadow: ({ boxShadows: { md } }) => md,
      }}
    >
      <Grid container spacing={2} alignItems="center">
        <Grid item>
          <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="3rem"
            height="3rem"
            borderRadius="lg"
            sx={{
              backgroundColor: ({ palette }) => palette[color || "info"].main,
              color: ({ palette: { white } }) => white.main,
            }}
          >
            <Icon fontSize="small">{icon}</Icon>
          </MDBox>
        </Grid>
        <Grid item xs>
          <MDBox>
            <MDTypography variant="caption" fontWeight="medium" color="text">
              {title}
            </MDTypography>
            <MDTypography variant="h6" fontWeight="bold">
              {typeof value === 'number' ? value.toFixed(2) : '-'} MW
            </MDTypography>
          </MDBox>
        </Grid>
      </Grid>
    </MDBox>
  );
}

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.number,
  icon: PropTypes.string.isRequired,
  color: PropTypes.string,
};

function CustomTooltip({ active, payload }) {
  if (active && payload && payload.length) {
    return (
      <MDBox
        sx={{
          backgroundColor: "white",
          padding: "10px",
          border: "1px solid #000",
          borderRadius: "4px",
        }}
      >
        <MDTypography variant="caption" fontWeight="medium">
          {payload[0].payload.name}
        </MDTypography>
        <MDBox>
          <MDTypography variant="caption" color="white">
            القيمة الفعلية: {payload[0].value.toFixed(2)} MW
          </MDTypography>
        </MDBox>
        <MDBox>
          <MDTypography variant="caption" color="white">
            القيمة المتوقعة: {payload[0].payload.expectedValue.toFixed(2)} MW
          </MDTypography>
        </MDBox>
        <MDBox>
          <MDTypography variant="caption" color="white">
            نسبة الإنتاج: {payload[0].payload.productionRate.toFixed(2)}%
          </MDTypography>
        </MDBox>
        <MDBox>
          <MDTypography variant="caption" color="white">
            الحالة: {payload[0].payload.status}
          </MDTypography>
        </MDBox>
      </MDBox>
    );
  }
  return null;
}

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
};

function TableChartView({ powerStationId, date }) {
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({
    color: "info",
    icon: "notifications",
    title: "تنبيه",
    content: "",
    open: false,
  });

  const fetchTableData = async () => {
    try {
      setLoading(true);
      const response = await RequestService.get(
        `PowerStation/table?PowerStationId=${powerStationId}&Date=${date}`
      );
      
      if (response.data && !response.data.error) {
        // Transform data for the chart
        const chartData = [];
        response.data.data.stGroups.forEach(group => {
          group.gtUnits.forEach(unit => {
            chartData.push({
              name: unit.gtName,
              value: unit.gtValue,
              expectedValue: unit.expectedStValue,
              productionRate: unit.productionRate,
              status: unit.status,
              powerGenerator: unit.powerGenerator,
            });
          });
        });
        setTableData({
          chartData,
          totalPowerGenerator: response.data.data.totalPowerGenerator,
          totalExpectedValue: response.data.data.totalExpectedValue,
          totalProductionRate: response.data.data.totalProductionRate,
        });
      } else {
        setNotification({
          color: "error",
          content: "حدث خطأ أثناء جلب البيانات",
          open: true,
        });
      }
    } catch (error) {
      console.error("Error fetching table data:", error);
      setNotification({
        color: "error",
        content: "حدث خطأ أثناء جلب البيانات",
        open: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (powerStationId && date) {
      fetchTableData();
    }
  }, [powerStationId, date]);

  if (loading) {
    return (
      <MDBox display="flex" justifyContent="center" p={3}>
        <MDTypography variant="button" color="text">
          جاري التحميل...
        </MDTypography>
      </MDBox>
    );
  }

  if (!tableData) {
    return (
      <MDBox display="flex" justifyContent="center" p={3}>
        <MDTypography variant="button" color="text">
          لا توجد بيانات متاحة
        </MDTypography>
      </MDBox>
    );
  }

  return (
    <Card>
      <MDBox p={3}>
        <MDBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <StatCard
                title="إجمالي الطاقة المولدة"
                value={tableData.totalPowerGenerator}
                icon="electric_bolt"
                color="success"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                title="إجمالي القيمة المتوقعة"
                value={tableData.totalExpectedValue}
                icon="trending_up"
                color="info"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                title="نسبة الإنتاج الكلية"
                value={tableData.totalProductionRate}
                icon="percent"
                color="warning"
              />
            </Grid>
          </Grid>
        </MDBox>
        <MDBox
          sx={{
            height: "400px",
            direction: "ltr"
          }}
        >
          <ResponsiveContainer width="100%" height="100%" >
            <BarChart
              data={tableData.chartData}
              margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                label={{ value: 'الوحدات', position: 'insideBottom', offset: -10 }}
                tickLine={false}
              />
              <YAxis
                label={{ value: 'القدرة (MW)', angle: -90, position: 'insideLeft' }}
              />

              <Tooltip content={<CustomTooltip />} />
              <Legend />
              
              <Bar
                dataKey="value"
                name="القيمة الفعلية"
                fill="#8884d8"
                radius={[4, 4, 0, 0]}
               
              />
              <ReferenceLine
                y={0}
                stroke="#000"
              />
            </BarChart>
          </ResponsiveContainer>
        </MDBox>
      </MDBox>
      <MDSnackbar
        color={notification.color}
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        open={notification.open}
        onClose={() => setNotification({ ...notification, open: false })}
        close={() => setNotification({ ...notification, open: false })}
      />
    </Card>
  );
}

TableChartView.propTypes = {
  powerStationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  date: PropTypes.string.isRequired,
};

export default TableChartView;
